import { useState, useMemo, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { categories, videoPlatforms, siteConfig } from '@/mocks/site';
import { trendingVideos, videoCollections } from '@/mocks/videos';
import { articles } from '@/mocks/articles';

const allCategoryTabs = [
  { slug: 'all', name: '全部', icon: 'ri-apps-line' },
  ...categories,
  { slug: 'shipinzixun', name: '视频资讯', icon: 'ri-newspaper-line' },
];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [activePlatform, setActivePlatform] = useState('all');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  const currentTab = allCategoryTabs.find((c) => c.slug === activeCategory) || allCategoryTabs[0];
  const isPlatform = useMemo(() => {
    return videoPlatforms.some((p) => p.slug === activeCategory);
  }, [activeCategory]);

  const filteredVideos = useMemo(() => {
    let videos = [...trendingVideos, ...videoCollections.map((c) => ({
      id: c.id,
      title: c.title,
      platform: c.platform,
      platformSlug: c.platformSlug,
      category: c.category,
      categorySlug: c.categorySlug,
      thumbnail: c.thumbnail,
      views: c.views,
      rating: c.rating,
    }))];

    if (isPlatform) {
      videos = videos.filter((v) => v.platformSlug === activeCategory);
    } else if (activeCategory !== 'all' && activeCategory !== 'shipinzixun') {
      videos = videos.filter((v) => v.categorySlug === activeCategory);
    }

    if (activePlatform !== 'all') {
      videos = videos.filter((v) => v.platformSlug === activePlatform);
    }

    return videos;
  }, [activeCategory, activePlatform, isPlatform]);

  const filteredArticles = useMemo(() => {
    if (activeCategory === 'shipinzixun') {
      return articles;
    }
    if (activeCategory !== 'all') {
      return articles.filter((a) => a.categorySlug === activeCategory);
    }
    return [];
  }, [activeCategory]);

  const categoryTitle = isPlatform
    ? videoPlatforms.find((p) => p.slug === activeCategory)?.name || currentTab.name
    : currentTab.name;

  const categoryDesc = isPlatform
    ? videoPlatforms.find((p) => p.slug === activeCategory)?.description || `青青草视频 - ${categoryTitle}`
    : `青青草视频 - ${categoryTitle}分类，浏览${categoryTitle}相关热门视频、影视资源与最新资讯。`;

  const categorySchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: siteConfig.url },
          { '@type': 'ListItem', position: 2, name: categoryTitle },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: `${categoryTitle} - ${siteConfig.name}`,
        description: categoryDesc,
        url: `${siteConfig.url}/category/${activeCategory}`,
        isPartOf: { '@type': 'WebSite', name: siteConfig.name, url: siteConfig.url },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${categoryTitle} - ${siteConfig.name}`}
        description={categoryDesc}
        keywords={`青青草视频,${categoryTitle},视频合集,热门视频`}
        canonicalUrl={`${siteConfig.url}/category/${activeCategory}`}
        schema={categorySchema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={[{ label: categoryTitle }]} />

            <header className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                {categoryTitle}
              </h1>
              <p className="text-sm text-foreground-600">{categoryDesc}</p>
            </header>

            <div className="flex flex-wrap items-center gap-1 mb-8">
              {allCategoryTabs.map((tab) => (
                <Link
                  key={tab.slug}
                  to={`/category/${tab.slug}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    activeCategory === tab.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                  onClick={() => {
                    setActiveCategory(tab.slug);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  <i className={`${tab.icon} text-xs`}></i>
                  {tab.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center gap-2 mb-6">
              <span className="text-sm text-foreground-500">平台筛选:</span>
              <button
                onClick={() => setActivePlatform('all')}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                  activePlatform === 'all'
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                全部平台
              </button>
              {videoPlatforms.map((p) => (
                <button
                  key={p.slug}
                  onClick={() => setActivePlatform(p.slug)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                    activePlatform === p.slug
                      ? 'bg-secondary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {p.name}
                </button>
              ))}
            </div>

            {activeCategory === 'shipinzixun' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group block rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 overflow-hidden"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={article.thumbnail} alt={article.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-3 text-xs text-foreground-500 mb-2">
                        <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700">{article.category}</span>
                        <span>{article.date}</span>
                        <span className="flex items-center gap-1"><i className="ri-eye-line"></i> {(article.views / 1000).toFixed(1)}k</span>
                      </div>
                      <h2 className="text-sm md:text-base font-semibold text-foreground-900 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                        {article.title}
                      </h2>
                      <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">{article.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {filteredVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.id}`}
                    className="group block rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 overflow-hidden"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img src={video.thumbnail} alt={video.title} title={`${video.title} - ${video.platform}`} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 rounded-full bg-background-50/90 backdrop-blur-sm text-xs font-medium text-foreground-800">{video.platform}</span>
                      </div>
                      <div className="absolute bottom-3 right-3 flex items-center gap-3 text-xs text-background-50/90">
                        <span className="flex items-center gap-1"><i className="ri-eye-line"></i> {(video.views / 10000).toFixed(1)}万</span>
                        <span className="flex items-center gap-1"><i className="ri-star-fill text-accent-400"></i> {video.rating}</span>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background-50/20 backdrop-blur-sm group-hover:bg-primary-500 transition-colors">
                          <i className="ri-play-fill text-background-50 text-2xl ml-0.5"></i>
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <h2 className="text-sm md:text-base font-semibold text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {video.title}
                      </h2>
                    </div>
                  </Link>
                ))}
                {filteredVideos.length === 0 && (
                  <div className="col-span-full py-20 text-center">
                    <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                      <i className="ri-inbox-line text-foreground-400 text-2xl"></i>
                    </div>
                    <p className="text-foreground-500">该分类暂无内容，请选择其他分类</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}